import { capitalizeTerm } from "@loot-os/api-contract";

import { useShopper } from "../../../../lib/shopper.js";
import { useTenant } from "../../../../lib/tenant.js";
import { useTerminology } from "../../../../lib/terminology.js";
import type { TemplateAction, TemplateLink } from "../../shared/links.js";

/** A promo card with buttons: they go in its footer, and the card itself is not a link. */
export interface PromoActionsCard {
  kind: "actions";
  key: string;
  /** A lead-in above the title; a card without one is its title alone. */
  eyebrow?: string;
  title: string;
  actions: TemplateAction[];
}

/** A promo card with nothing to press: the whole card is the link. */
export interface PromoLinkCard {
  kind: "link";
  key: string;
  /** A lead-in above the title; a card without one is its title alone. */
  eyebrow?: string;
  title: string;
  destination: TemplateLink;
}

export type PromoCard = PromoActionsCard | PromoLinkCard;

export interface PromoCardsContent {
  cards: PromoCard[];
}

/**
 * Sign-up while signed out, the boxes, and battles when the tenant has them on. No free-pack
 * offer: the design has one, but nothing in loot-os grants it.
 */
export function usePromoCardsContent(): PromoCardsContent {
  const { user } = useShopper();
  const { hasFeature } = useTenant();
  const { box, battle } = useTerminology();

  const cards: PromoCard[] = [];
  if (!user) {
    cards.push({
      kind: "actions",
      key: "sign-up",
      eyebrow: "Create an account",
      title: "Start collecting",
      actions: [
        { label: "Sign up", emphasis: "primary", to: "/login" },
        { label: "Log in", emphasis: "secondary", to: "/login" },
      ],
    });
  }
  cards.push({
    kind: "link",
    key: "boxes",
    title: `Rip ${box.plural}`,
    destination: { to: "/boxes" },
  });
  if (hasFeature("battles")) {
    cards.push({
      kind: "link",
      key: "battles",
      title: capitalizeTerm(battle.plural),
      destination: { to: "/battles" },
    });
  }
  return { cards };
}
